/**
 * NFL Card Rarity → Display Class (jest-client mirror)
 *
 * Local replica of the webapp's services/cards/nfl-rarity — the jest-client
 * cannot import across apps, so the small pure mapping lives here. Keep in
 * sync with apps/nextjs/src/services/cards/nfl-rarity.ts.
 *
 * NFL Card Leagues mint exactly three CardRarity values and present them as a
 * three-tier display class (Common / Rare / Legendary). The class is a shared
 * source of truth for FE presentation (border/chip) AND for server-side league
 * gates, which count cards *cumulatively* by class: a Legendary card counts
 * toward both a min-Rare and a min-Legendary requirement.
 *
 * Mapping (product decision — ideation Q2):
 *   PROSPECT → COMMON · VETERAN → RARE · GOAT → LEGENDARY
 * ROOKIE / ALL_STAR are not minted for NFL but map as sensible fallbacks
 * (ROOKIE → COMMON, ALL_STAR → RARE) so a stray legacy card never crashes a read.
 */

import type { CardRarity } from '@prisma/client';

/** The three NFL display classes, ascending. */
export type DisplayClass = 'COMMON' | 'RARE' | 'LEGENDARY';

/** The exact CardRarity values NFL packs mint. */
export const NFL_MINTED_RARITIES: CardRarity[] = ['PROSPECT', 'VETERAN', 'GOAT'];

/** Ascending rank so cumulative gate math is a simple ≥ comparison. */
const CLASS_RANK: Record<DisplayClass, number> = {
  COMMON: 0,
  RARE: 1,
  LEGENDARY: 2,
};

const RARITY_TO_CLASS: Record<CardRarity, DisplayClass> = {
  PROSPECT: 'COMMON',
  ROOKIE: 'COMMON',   // not minted for NFL — fallback
  VETERAN: 'RARE',
  ALL_STAR: 'RARE',   // not minted for NFL — fallback middle
  GOAT: 'LEGENDARY',
};

/** Map a CardRarity to its NFL display class. */
export function displayClass(rarity: CardRarity): DisplayClass {
  return RARITY_TO_CLASS[rarity];
}

/**
 * Cumulative gate test: does this rarity count toward a `cls`-or-better
 * requirement? (e.g. classAtLeast('GOAT', 'RARE') === true — a Legendary
 * counts toward min-Rare.)
 */
export function classAtLeast(rarity: CardRarity, cls: DisplayClass): boolean {
  return CLASS_RANK[displayClass(rarity)] >= CLASS_RANK[cls];
}

/** Count how many of the given rarities meet a `cls`-or-better requirement. */
export function countAtLeast(rarities: CardRarity[], cls: DisplayClass): number {
  return rarities.reduce((n, r) => (classAtLeast(r, cls) ? n + 1 : n), 0);
}

/**
 * Cumulative class counts for a set of cards, used to evaluate league gates
 * `{ minRare, minLegendary }`. haveRare includes Legendary cards; haveLegendary
 * counts only Legendary.
 */
export function classCounts(rarities: CardRarity[]): { haveRare: number; haveLegendary: number } {
  return {
    haveRare: countAtLeast(rarities, 'RARE'),
    haveLegendary: countAtLeast(rarities, 'LEGENDARY'),
  };
}
